import type { JSONContent } from '@tiptap/react';

import { EDataDefinitionAttributes, Id, Timestamp } from 'ev-types';

import { FormTrigger } from 'ev-api/core/customFormSubmissions';

export enum FieldType {
  ShortText = 'short_text',
  LongText = 'long_text',
  SingleSelection = 'single_selection',
  MultiSelection = 'multi_selection',
  Date = 'date_picker',
  DateTime = 'date_time',
  Email = 'email',
  Phone = 'phone',
  Number = 'number',
  Boolean = 'boolean',
  Dropdown = 'dropdown',
  TextBlock = 'text_block',
  Agreement = 'agreement',
  Calculator = 'calculator',
}

export enum ChildFieldType {
  Checkbox = 'checkbox',
  Signature = 'signature',
}

export type CustomFormSubscriptionCounts = {
  VisitType: number;
  Practice?: number;
};

export type CustomFormSubscription = {
  id: Id;
  custom_form_id: Id;
  custom_form_version_id: Id;
  subscriber_type: 'VisitType' | 'Practice' | string;
  subscriber_id: Id;
  event_trigger: FormTrigger;
  is_active: boolean;
};

type CalculatorRule = {
  operator: string;
  value: string;
  result: string;
};

export type CustomFormFieldDatasetItem = {
  value: string;
  label?: string;
  formula_value?: number;
  calculator?: {
    formula?: JSONContent;
    rules?: CalculatorRule[];
  };
};

export type CustomFormFieldDataset = CustomFormFieldDatasetItem[] | null;

export type AgreementData = {
  agreements?: Record<string, string>;
  checkboxes?: Array<{
    definition_id: Id;
    label: string;
    required: boolean;
  }>;
  esignature?: {
    definition_id: Id;
    required?: boolean;
    label?: string;
    hint?: string;
  };
};

export type CustomFormField = {
  label: string;
  field_type: FieldType;
  dataset: CustomFormFieldDataset;
  description: string | null;
  hint: string | null;
  id: Id;
  custom_form_id: Id;
  edata_definition_id: Id;
  is_visible: boolean;
  is_required: boolean;
  is_editable: boolean;
  is_archived: boolean;
  is_sensitive: boolean;
  config_options: {
    conditional_logic?: string;
    other_option?: boolean;
    show_values?: boolean;
    show_formula_values?: boolean;
  } | null;
  filtered_conditional_logic?: string;
  sort: number;
  row_sort: number;
  value: string | null;
  valueId?: string;
  data_type: string;
  default_value: string | null;
  created_at: Timestamp;
  updated_at: Timestamp;
  edata_definition: EDataDefinitionAttributes;
  system_datapoint?: string | null;
  field_conditioned_by?: Array<{ id: Id; value: string }> | null;
  non_translatable?: boolean;
};

export type CustomForm = {
  id: Id;
  uuid: string;
  customer_id: Id;
  title: string;
  description: string;
  original_form_id: Id | null;
  is_archived: boolean;
  unsaved_data: boolean | null;
  created_at: Timestamp;
  updated_at: Timestamp;
  subscription_counts?: CustomFormSubscriptionCounts;
  fields: CustomFormField[];
  is_published: boolean;
  subscriptions: CustomFormSubscription[];
  locale?: string;
};
